/**
 * The layered tail drive — ADR-038 R5 (ruled 2026-09-24): **the spring
 * follows the animation.** Allen: "Will the springs create unrealistic
 * motions? Does it need something more combinatorial to split the divide
 * between self-driven tail motion and physics-driven? Cat woman flips but
 * also wags her tail."
 *
 * The answer the field converged on (docs/research/LAYERED_DRIVE_AND_PHYSICS_
 * RESEARCH_2026-09-24.md — VRChat PhysBones "Is Animated", Magica "Animation
 * Pose Ratio", Chaos "Anim Drive", UE Physics Control, active ragdolls,
 * Deformable Objects Alive!): **intent goes in the TARGET, dynamics come from
 * the solve.** Self-driven and physics-driven are not two regimes to switch
 * between; they are two layers of one step.
 *
 * ONE responsibility: the engine-free half — the moods (targets AND
 * parameters), the per-joint drive gains, the dynamics limits, the settings a
 * person moves (the Document slice), and the drive block a file declares
 * beside `VRMC_springBone`. The adapter composes quaternions and runs the
 * solver; World ports the same arithmetic from the file's declaration.
 *
 * THE STEP (fixed rate, every quantity sampled at the STEP's time t):
 *   1. ANIMATED POSE: each tail bone's local rotation as the animation leaves
 *      it at t — authored keys where a clip keys the bone, its rest elsewhere.
 *   2. TARGET = animated ⊕ mood(t) ⊕ reflex(body): the mood's carriage,
 *      base oscillator and tip twitch post-multiplied in each bone's own frame
 *      (about the declared yaw / pitch axes); the reflex counter-rotates the
 *      base against the body's smoothed angular velocity (behind a flag).
 *      KEYS FIRST, MOOD ADDITIVE ON TOP — `loose` adds nothing.
 *   3. INERTIA: the chain's Verlet state is carried along with the body by
 *      the share of this step's body motion the tail does NOT inherit
 *      (`1 − inertia`, and whatever exceeds the inherit speed caps); a body
 *      that moves further than the teleport thresholds in one step RESETS the
 *      chain onto its target.
 *   4. SPRING: the reference `VRMC_springBone` joint update with its initial
 *      local frame re-seated to the TARGET (stiffness pulls toward the
 *      animation, not the bind), the particle speed capped first.
 *   5. MUSCLE: a drive GAIN, not an output blend — the joint closes a share
 *      `1 − 2^(−h/halfLife)` of its angle to the target, capped at
 *      `maxDegPerS · h` (the muscle budget: a violent body move saturates it
 *      and the tail visibly loses the fight for a moment). The Verlet tail is
 *      written back — one state, no second pose to slide between — and
 *      `muscleMomentum` of the correction becomes velocity (all of it rings
 *      the base about its target; none drags the distal joints along with no
 *      momentum; measured on the kobold, 0.35).
 *   6. LIMITS: a cone of `coneDeg` about the target; a joint past it is set on
 *      the cone and its velocity CANCELLED (overshoot absorbed, not bounced);
 *      the colliders run again on the corrected tail. Length never stretches
 *      (the reference constraint).
 *
 * GAINS (the tail-anatomy prior, corrected by the research): the muscle
 * bellies sit at the base but their long tendons actuate the tip (Wada 1994),
 * so a gain PROFILE per mood — base / mid / tip, piecewise linear down the
 * chain — times the one `muscle` knob: `g_k = min(1, 2·muscle·profile(t_k))`,
 * `halfLife_k = tightHalfLifeS / g_k` (g = 0: no drive). Muscle 0.5 wears the
 * mood's profile as written; 0 is physics only; 1 doubles it (saturating).
 *
 * Depends on: zod. Depended on by: the Document (edits.tailSpring), the
 * declaration's drive block, adapter-three's runtime drive, the app's spring.
 */
import { z } from 'zod';

const finite = z.number().finite();
const vec3 = z.tuple([finite, finite, finite]);

/** The mood set, closed. */
export const TAIL_MOODS = ['wag', 'lash', 'tuck', 'still', 'twitch', 'loose'] as const;
export const tailMoodSchema = z.enum(TAIL_MOODS);
export type TailMood = z.infer<typeof tailMoodSchema>;

/** The name of the mood formulas below — a file that declares a mood names the set it means. */
export const TAIL_MOOD_SET = 'tail-moods@1';

// ---------------------------------------------------------------------------
// The moods: targets AND parameters
// ---------------------------------------------------------------------------

const gainProfileSchema = z.object({ base: finite.min(0).max(1), mid: finite.min(0).max(1), tip: finite.min(0).max(1) });
export const moodDefinitionSchema = z.object({
  /** Drive gain down the chain, before the muscle knob. */
  gain: gainProfileSchema,
  /** Held carriage: total pitch in degrees (+ = DOWN), spread over the proximal `carriageReach` (0–1) of the chain. */
  pitchDeg: finite,
  carriageReach: finite.positive().max(1),
  /** The base oscillator (yaw), driven on the first `joints` bones ONLY — the spring carries the wave. */
  osc: z
    .object({
      /** Total amplitude over the driven joints, degrees. */
      ampDeg: finite.nonnegative(),
      hz: finite.positive(),
      /** Phase lag per driven joint, radians. */
      lagRad: finite,
      /** Waveform `sign(s)·|s|^sharpness` (1 = a sine; below 1 snaps). */
      sharpness: finite.positive().max(1),
      /** Burst envelope `0.55 + 0.45·sin(2π·burstHz·t)`; 0 = steady. */
      burstHz: finite.nonnegative(),
      joints: z.number().int().min(1),
    })
    .optional(),
  /** A tip-only yaw twitch over t ≥ `from`: a steady sine (`hz`) or pulses (`cycleS`, `at`, `widthS`, alternating sides). */
  twitch: z
    .object({
      ampDeg: finite.nonnegative(),
      from: finite.min(0).max(1),
      hz: finite.positive().optional(),
      cycleS: finite.positive().optional(),
      at: z.array(finite.nonnegative()).optional(),
      widthS: finite.positive().optional(),
    })
    .optional(),
});
export type MoodDefinition = z.infer<typeof moodDefinitionSchema>;

/**
 * tail-moods@1, as numbers (the file carries this table, so a reader sets a
 * mood without guessing its formula). The gain profiles follow the research's
 * §4 taxonomy: lash is STIFF (high gain), loose is low, still is high and
 * near critically damped, tuck holds high everywhere (it rests on the leg
 * colliders), twitch actuates the tip (the tendons). Starting points for
 * Allen's eye, not measurements of any animal.
 */
export const TAIL_MOOD_TABLE: Readonly<Record<TailMood, MoodDefinition>> = {
  /** happy: a lateral wave from the base, carriage raised a little. */
  wag: { gain: { base: 0.8, mid: 0.3, tip: 0.25 }, pitchDeg: -12, carriageReach: 0.45, osc: { ampDeg: 30, hz: 1.4, lagRad: 0.3, sharpness: 1, burstHz: 0, joints: 3 } },
  /** irritated: stiff, fast, snapping, in bursts. */
  lash: { gain: { base: 1, mid: 0.75, tip: 0.5 }, pitchDeg: 0, carriageReach: 0.45, osc: { ampDeg: 55, hz: 2.2, lagRad: 0.35, sharpness: 0.35, burstHz: 0.4, joints: 3 } },
  /** scared: pulled down and in — a held curl under, held hard. */
  tuck: { gain: { base: 1, mid: 0.9, tip: 0.8 }, pitchDeg: 85, carriageReach: 0.8 },
  /** hunting: held low and straight, near critically damped; a tip flick each way per 2.3 s. */
  still: { gain: { base: 0.9, mid: 0.8, tip: 0.6 }, pitchDeg: 10, carriageReach: 0.45, twitch: { ampDeg: 25, from: 0.6, cycleS: 2.3, at: [0.35, 1.25], widthS: 0.2 } },
  /** play / mild irritation: neutral carriage, a fast small twitch of the tip only. */
  twitch: { gain: { base: 0.8, mid: 0.5, tip: 0.8 }, pitchDeg: 0, carriageReach: 0.45, twitch: { ampDeg: 18, from: 0.65, hz: 3 } },
  /** drive off: no target offset, no muscle — physics only. */
  loose: { gain: { base: 0, mid: 0, tip: 0 }, pitchDeg: 0, carriageReach: 0.45 },
};

/** The wag's person-facing knobs (the research: amplitude, frequency, left/right bias). */
export const wagKnobsSchema = z.object({
  amplitudeDeg: finite.min(0).max(120),
  hz: finite.min(0.1).max(6),
  /** −1 … 1: the wag's centre pushed to one side by half its amplitude at full bias. */
  bias: finite.min(-1).max(1),
});
export type WagKnobs = z.infer<typeof wagKnobsSchema>;
/**
 * The wag a tail ships with until a person tunes it. 0.5 Hz / 45° is Allen's own hand-tuned ape (Gruber,
 * 2026-10-03, the same 0.93 m Tripo body): the old 1.4 Hz / 30° read "haywire" in World on the ape the guided
 * intake gave bones — a wag nobody had tuned, played at 2.8× the speed he chose. World reads `hz` as wags
 * per second, exactly as Harry writes it (springDrive.ts: sin(2π·hz·t)); the value was the fault, not the
 * reading. The mood table's wag row is the formula's reference; these knobs are what a tail carries.
 */
export const TAIL_WAG_DEFAULTS: WagKnobs = { amplitudeDeg: 45, hz: 0.5, bias: 0 };

/** Degrees → radians, spelled once. */
const RAD = Math.PI / 180;

/** Per-bone shares (summing to 1) of a profile over `n` bones, t = k/(n−1). */
function shares(n: number, profile: (t: number, k: number) => number): number[] {
  const raw = Array.from({ length: n }, (_, k) => profile(n === 1 ? 0 : k / (n - 1), k));
  const sum = raw.reduce((a, b) => a + b, 0);
  return sum === 0 ? raw : raw.map((v) => v / sum);
}

/** A bump of width `w` seconds centred at `c`, sin² shaped, 0 elsewhere. */
function bump(t: number, c: number, w: number): number {
  const u = (t - c) / w + 0.5;
  return u <= 0 || u >= 1 ? 0 : Math.sin(Math.PI * u) ** 2;
}

/**
 * One mood's per-bone angles at time `t` (radians), root → tip, over `n`
 * bones. `wag` knobs apply to the wag's oscillator (amplitude, frequency,
 * bias); other moods wear their table row.
 */
export function moodAngles(mood: TailMood, t: number, n: number, wag: WagKnobs = TAIL_WAG_DEFAULTS): { yaw: number[]; pitch: number[] } {
  const def = TAIL_MOOD_TABLE[mood];
  const yaw = Array.from({ length: n }, () => 0);
  const pitch = shares(n, (u) => Math.max(0, 1 - u / def.carriageReach)).map((s) => def.pitchDeg * RAD * s);
  if (def.osc !== undefined) {
    const o = mood === 'wag' ? { ...def.osc, ampDeg: wag.amplitudeDeg, hz: wag.hz } : def.osc;
    const bias = mood === 'wag' ? wag.bias : 0;
    const driven = Math.min(o.joints, n);
    const s = shares(n, (_, k) => (k < driven ? driven - k : 0));
    const envelope = o.burstHz > 0 ? 0.55 + 0.45 * Math.sin(2 * Math.PI * o.burstHz * t) : 1;
    s.forEach((sk, k) => {
      if (sk === 0) return;
      const w = Math.sin(2 * Math.PI * o.hz * t - k * o.lagRad);
      const shaped = o.sharpness === 1 ? w : Math.sign(w) * Math.abs(w) ** o.sharpness;
      yaw[k] = o.ampDeg * RAD * sk * envelope * (shaped + 0.5 * bias);
    });
  }
  if (def.twitch !== undefined) {
    const tw = def.twitch;
    let drive = 0;
    if (tw.hz !== undefined) drive = Math.sin(2 * Math.PI * tw.hz * t);
    else if (tw.cycleS !== undefined && tw.at !== undefined && tw.widthS !== undefined) {
      const phase = ((t % tw.cycleS) + tw.cycleS) % tw.cycleS;
      tw.at.forEach((c, i) => (drive += (i % 2 === 0 ? 1 : -1) * bump(phase, c, tw.widthS!)));
    }
    shares(n, (u) => Math.max(0, (u - tw.from) / (1 - tw.from))).forEach((sk, k) => (yaw[k] = yaw[k]! + tw.ampDeg * RAD * sk * drive));
  }
  return { yaw, pitch };
}

/**
 * The per-sprung-joint drive gains (`joints` = chain bones − 1), in [0, 1]:
 * the mood's base / mid / tip profile, piecewise linear at t = k / joints,
 * times the muscle knob (`min(1, 2·muscle·profile)`).
 */
export function moodGains(mood: TailMood, muscle: number, joints: number): number[] {
  if (!(muscle >= 0 && muscle <= 1)) throw new RangeError(`muscle ${muscle} is outside [0, 1]`);
  const g = TAIL_MOOD_TABLE[mood].gain;
  return Array.from({ length: joints }, (_, k) => {
    const t = joints <= 1 ? 0 : k / joints;
    const p = t <= 0.5 ? g.base + (g.mid - g.base) * (t / 0.5) : g.mid + (g.tip - g.mid) * ((t - 0.5) / 0.5);
    return Math.min(1, 2 * muscle * p);
  });
}

/** A gain → its half-life (seconds), or null for no drive. */
export function gainHalfLife(gain: number, tightHalfLifeS: number): number | null {
  return gain > 0 ? tightHalfLifeS / gain : null;
}

/** Seconds a mood change cross-fades over (smoothstep on the angles AND the gains). */
export const TAIL_MOOD_FADE_SECONDS = 0.25;
/** A smoothstep 0 → 1 over the fade. */
export function moodFade(since: number): number {
  const s = Math.min(1, Math.max(0, since / TAIL_MOOD_FADE_SECONDS));
  return s * s * (3 - 2 * s);
}

// ---------------------------------------------------------------------------
// The dynamics limits (declared numbers; the solver's clamps and resets)
// ---------------------------------------------------------------------------

export const springDynamicsSchema = z.object({
  /** The fixed step, Hz. */
  stepHz: finite.positive(),
  /** A gain-1 joint's half-life toward its target, seconds. */
  tightHalfLifeS: finite.positive(),
  /** The muscle budget: the most a joint's drive turns it per second, degrees. */
  maxDegPerS: finite.positive(),
  /**
   * The share (0–1) of the muscle's correction that becomes velocity: 0 = a
   * pure position servo (overdamped: the base never rings, but the distal
   * joints it drags get no momentum), 1 = all of it (underdamped: the base
   * rings about its target).
   */
  muscleMomentum: finite.min(0).max(1),
  /** Each joint's cone about its target, degrees; past it the joint is set on the cone and its velocity cancelled. */
  coneDeg: finite.positive().max(180),
  /** The share (0–1) of the body's per-step motion the tail's Verlet state inherits; the rest carries it along. */
  inertia: finite.min(0).max(1),
  /** Caps on the INHERITED body motion: linear m/s, angular degrees/s. */
  maxInheritSpeed: finite.positive(),
  maxInheritDegPerS: finite.positive(),
  /** Cap on each joint tail's own speed, m/s. */
  maxParticleSpeed: finite.positive(),
  /** A body that moves further than either in ONE step resets the chain onto its target (a teleport, a cut, a loop's root jump). */
  teleportMeters: finite.positive(),
  teleportDeg: finite.positive(),
  /** The reflex (balance) layer: target counter-rotation = −gainS · ω (smoothed by `smoothHalfLifeS`), capped at `maxDeg`, over the first `joints` bones. */
  reflex: z.object({ gainS: finite.nonnegative(), maxDeg: finite.nonnegative(), smoothHalfLifeS: finite.positive(), joints: z.number().int().min(1) }),
});
export type SpringDynamics = z.infer<typeof springDynamicsSchema>;

/**
 * The defaults. Teleport 1 m / 90° is the research's starting guess (Magica's
 * two thresholds). A starting point, tuned by measurement
 * (`tail-drive-probe.test.ts`), not a claim about any animal.
 */
export const TAIL_DYNAMICS_DEFAULTS: SpringDynamics = {
  stepHz: 60,
  tightHalfLifeS: 0.01,
  maxDegPerS: 720,
  muscleMomentum: 0.35,
  coneDeg: 30,
  inertia: 0.3,
  maxInheritSpeed: 1.5,
  maxInheritDegPerS: 360,
  maxParticleSpeed: 10,
  teleportMeters: 1,
  teleportDeg: 90,
  reflex: { gainS: 0.12, maxDeg: 40, smoothHalfLifeS: 0.05, joints: 3 },
};

// ---------------------------------------------------------------------------
// The settings (the Document slice, R3) and the file's drive block
// ---------------------------------------------------------------------------

/** Muscle's default: every mood wears its profile as written. */
export const TAIL_MUSCLE_DEFAULT = 0.5;
/** The mood a tail starts in: the preview wag, now a driver (Allen: the wag plays on preview no matter the animation). */
export const TAIL_MOOD_DEFAULT: TailMood = 'wag';

export const tailSpringSettingsSchema = z.object({
  /** The spring plays (layered) — off, the drive plays kinematically (no physics). */
  on: z.boolean(),
  stiffness: finite.min(0).max(4),
  drag: finite.min(0).max(1),
  gravity: finite.min(0).max(0.5),
  muscle: finite.min(0).max(1),
  mood: tailMoodSchema,
  wag: wagKnobsSchema,
  /** The share of body motion the tail inherits (0 = carried with the body, 1 = full world inertia). */
  inertia: finite.min(0).max(1),
  /** The reflex (balance) layer on — measured, off by default until Allen's viewport read. */
  reflex: z.boolean(),
  confirmedBy: z.string().regex(/^human:\S+$/),
});
export type TailSpringSettings = z.infer<typeof tailSpringSettingsSchema>;

/**
 * THE PADDLE (ADR-038 §13.7): a FLAT tail — a beaver's — bends about its WIDTH
 * (up and down across its thin axis) and resists bending sideways and twisting.
 * Per sprung joint: the hinge axis (the flesh's width direction at that
 * segment, in the bone's LOCAL bind frame); each step the joint's deviation
 * from its target is split (swing-twist) into the part about the hinge — free
 * up to `maxDeg` — and the rest, which is pulled back with a `offAxisHalfLifeS`
 * half-life and never exceeds `offAxisMaxDeg`. Enabled automatically when the
 * flesh measures flat (median cross-section aspect ≥ `TAIL_FLAT_ASPECT`); a
 * round tail declares none.
 */
export const tailHingeSchema = z.object({
  /** Per sprung joint, unit, bone-local bind frame. */
  axes: z.array(vec3),
  maxDeg: finite.positive().max(180),
  offAxisMaxDeg: finite.nonnegative().max(180),
  offAxisHalfLifeS: finite.positive(),
  /** Per sprung joint: the measured cross-section aspect (width / thickness) — provenance. */
  aspect: z.array(finite.positive()),
});
export type TailHinge = z.infer<typeof tailHingeSchema>;
/** Median cross-section aspect at and above which a tail is a paddle (Bonnie Beaver measures 2.7; a round tail ~1–1.4). */
export const TAIL_FLAT_ASPECT = 1.8;
export const TAIL_HINGE_DEFAULTS = { maxDeg: 30, offAxisMaxDeg: 6, offAxisHalfLifeS: 0.02 } as const;

/**
 * What a file declares beside `VRMC_springBone` (in the spring's own
 * `extras.poqpoq.drive`). The standard extension CANNOT say this: its update
 * writes `initialLocalRotation · fromTo(...)`, discarding any animation on the
 * joint's own rotation, and it has no reset or speed-limit contract. So the
 * layered semantics ride beside it, declared as numbers — the target is the
 * ANIMATED pose sampled at each fixed step's time, the mood table the reader
 * sets moods from, the per-joint gains and half-lives for the declared mood
 * (recorded, never re-derived), the dynamics limits, and each bone's drive
 * axes in its own local bind frame. A reader that ignores the block plays the
 * standard passive spring against the bind.
 */
export const springDriveSchema = z.object({
  target: z.literal('animated-pose'),
  moodSet: z.literal(TAIL_MOOD_SET),
  /** The mood a reader starts in; a reader may set another from its own state (the mood input). */
  mood: tailMoodSchema,
  muscle: finite.min(0).max(1),
  wag: wagKnobsSchema,
  reflex: z.boolean(),
  /** Per sprung joint, root → last sprung joint, for the declared mood: the gain, and its half-life (null = no drive). */
  gains: z.array(finite.min(0).max(1)),
  halfLivesS: z.array(finite.positive().nullable()),
  dynamics: springDynamicsSchema,
  moods: z.record(tailMoodSchema, moodDefinitionSchema),
  /** Per chain bone (root → tip): the yaw (body up) and pitch (down) axes in the bone's LOCAL bind frame, unit. */
  axes: z.array(z.object({ yaw: vec3, pitch: vec3 })),
  /** A flat tail's hinge (§13.7); absent = a round tail, no hinge. */
  hinge: tailHingeSchema.optional(),
});
export type SpringDrive = z.infer<typeof springDriveSchema>;

/** The drive block for settings over a chain of `bones` bones with measured axes. */
export function springDriveDeclaration(
  settings: Pick<TailSpringSettings, 'mood' | 'muscle' | 'wag' | 'inertia' | 'reflex'>,
  axes: SpringDrive['axes'],
  hinge?: TailHinge | null,
): SpringDrive {
  const dynamics = { ...TAIL_DYNAMICS_DEFAULTS, inertia: settings.inertia };
  const gains = moodGains(settings.mood, settings.muscle, axes.length - 1);
  return springDriveSchema.parse({
    target: 'animated-pose',
    moodSet: TAIL_MOOD_SET,
    mood: settings.mood,
    muscle: settings.muscle,
    wag: settings.wag,
    reflex: settings.reflex,
    gains,
    halfLivesS: gains.map((g) => gainHalfLife(g, dynamics.tightHalfLifeS)),
    dynamics,
    moods: TAIL_MOOD_TABLE,
    axes,
    ...(hinge !== undefined && hinge !== null ? { hinge } : {}),
  });
}
